/**
 * Open-water vs pool copy vocabulary — the ONE place the display layer decides
 * whether a swim's rows are "splits" (open water: GPS-distance windows) or
 * "lengths" (pool: fixed wall-to-wall lengths). Every view that renders a per-
 * swim caption reads from here so the language can never disagree between the
 * hero, the ribbon, the HR timeline, and the Scores per-swim cards.
 *
 * HARD REGRESSION GUARANTEE: for a pool swim (`isOpenWater === false`) every
 * getter returns the EXACT string the views shipped before open water existed,
 * byte-for-byte. The open-water branch is the only new copy.
 */

export interface SwimVocab {
  /** Singular unit word: "split" (open water) | "length" (pool). */
  unit: string;
  /** Plural unit word: "splits" | "lengths". */
  units: string;
}

export function swimVocab(isOpenWater: boolean): SwimVocab {
  return isOpenWater
    ? { unit: "split", units: "splits" }
    : { unit: "length", units: "lengths" };
}

/** SetRibbonSection header ("Your swim, as sets" for pool). */
export function ribbonHeader(isOpenWater: boolean, showcase: boolean): string {
  if (isOpenWater) return showcase ? "My swim, as splits" : "Your swim, as splits";
  return showcase ? "My swim, as sets" : "Your swim, as sets";
}

/** SetRibbonSection static explainer beneath the header. */
export function ribbonCaption(isOpenWater: boolean): string {
  return isOpenWater
    ? "One continuous swim, split by distance."
    : "Sets are split by rests of 15 seconds or more.";
}

/** HrTimelineChart strip caption. */
export function hrStripCaption(isOpenWater: boolean, restCount = -1): string {
  // Open water: one continuous swim → the strip is ONE solid bar, which reads
  // as page chrome unless the caption points straight at it; and "rests" is
  // the wrong word for the low-effort wade-in/sighting stretches the HR-valley
  // pass marks (persona gate 2026-07-11, B5+B7 — 4/5 read the bar as missing
  // and the dashed block as a glitch). The block sentence agrees with what is
  // actually drawn: singular for one block, absent for none (round 2 polish —
  // "blocks" plural over a single block read as under-rendering). restCount<0
  // (caller unaware) keeps the plural.
  if (isOpenWater) {
    const barPart = "The bar on top is your main stroke.";
    if (restCount === 0) return barPart;
    if (restCount === 1) return `${barPart} The faint block is where you eased off.`;
    return `${barPart} Faint blocks are where you eased off.`;
  }
  if (restCount === 0) return "Colored band shows each set's main stroke.";
  if (restCount === 1) return "Colored band shows each set's main stroke. The faint block is a rest.";
  return "Colored band shows each set's main stroke. Faint blocks are rests.";
}

/** ScoresTab "Heart rate" review-card note. */
export function hrCardNote(isOpenWater: boolean): string {
  return isOpenWater
    ? "Your effort across the whole swim."
    : "Your effort across the whole swim, set by set.";
}

/** ScoresTab "Strokes and time per length/split" review-card headline. */
export function strokeTimeHeadline(isOpenWater: boolean): string {
  return isOpenWater ? "Strokes and time per split" : "Strokes and time per length";
}

/** ScoresTab "Strokes and time" review-card note. */
export function strokeTimeNote(isOpenWater: boolean): string {
  return isOpenWater
    ? "This swim, by stroke."
    : "This swim, by stroke. Drills aren't counted here.";
}
